
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import cloneDeep from "lodash/cloneDeep"
import Guid from "@/scripts/guid"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"

import SuggestList from "@/components/Fields/SuggestList.vue"

import User from "@/types/user"
import { Tag, DataType, CalculationDataTypeValue, CalculationType, Team, Field } from "@/types"

import apiTag from "@/services/api_Tag"
import api from "@/services/api"

import { operatorsByFieldType, operatorLabels, dataTypeByFieldType, FormattingRule } from "@/types/insights"
import { CaseColumn, caseColumns } from "@/types/caseColumns"

import InsightFilters from "@/components/Insights/InsightFilters.vue"

import { View, Clause, DayOfWeek, getDefaultSearchClauseParameters } from "@/types/view"

import {
	groupByOptions
} from "@/types/dataType"

import ColumnSelect from "@/components/Controls/ColumnSelect.vue"
import FolderSetup from "./FolderSetup.vue"
import { Folder, FolderType } from "@/types/folder"
import { UserColumn } from "@/types/userColumns"

const setup = namespace("setup")

interface FieldOption {
	type: string;
	title: string;
	id: string;
	fieldDataType: DataType
}

@Component({
	components: {
		"v-select": vSelect,
		scrollbar: VuePerfectScrollbar,
		"data-table": DataTable,
		"folder-setup": FolderSetup,
		skeleton: SkeletonLoader
	}
})
export default class ViewSetup extends Vue {
	@setup.Action loadDefinitions!: () => Promise<any[]>;

	@setup.Getter("getFieldTypesById")
	fieldTypesById!: { [key: string]: any };

	@setup.Getter("getDefinitionsById")
	public definitionsById!: any;

	@setup.Getter("getTeams")
	teams!: Team[];

	@setup.Getter("getFilterableFields")
	getFilterableFields!: CaseColumn[];

	@setup.Getter("getFilterableUserFields")
	getFilterableUserFields!: UserColumn[];

	@setup.Action("loadFields")
	loadFields!: () => Promise<void>;

	private loading: boolean = false;
	private views: View[] = [];
	private folders: Folder[] = []

	private editView: View | null = null;

	public fields: CaseColumn[] = [];
	public userFields: UserColumn[] = [];

	public showFolders: boolean = false
	public folderType: FolderType = FolderType.View

	public fieldsForDropdown: FieldOption[] = [];
	public groupByDropdown: FieldOption[] = [];
	public sortByDropdown: FieldOption[] = [];

	public caseColumnsDictionary: any = null;

	private readonly fieldsToSuppressFromFiltering: string[] = ["Closed", "Closed At"];

	public get filteredFields (): CaseColumn[] {
		return this.fields.filter(field => !this.fieldsToSuppressFromFiltering.includes(field.label))
	}

	public async mounted () {
		// Load views, folders, fields and data source items

		await Promise.all([
			this.loadViews(),
			this.loadFolders()
		])

		// just get fields from Vuex
		await this.loadFields()

		this.fields = this.getFilterableFields
		this.userFields = this.getFilterableUserFields

		this.caseColumnsDictionary = cloneDeep(caseColumns)
		this.caseColumnsDictionary = this.caseColumnsDictionary.toDictionary("key")

		// fields with properties specifically for group by and sort by fields
		this.fieldsForDropdown = [
			...this.fields
				.map((f: any) => {
					return {
						type: f.dataType,
						title: f.label,
						description: f.description,
						id: f.key,
						itemLabel: f.listLabel,
						fieldDataType: f.fieldDataType
					}
				})
				.sortBy("title")
		]

		// Filter fields to only include data types used for grouping
		this.groupByDropdown = [
			...this.fieldsForDropdown.filter((e: any) => {
				return (
					e.type !== "list-data" &&
					e.type !== "list-multiple" &&
					e.type !== "text-area" &&
					e.type !== "text"
				)
			})
		]

		// Filter fields to only include data types used for sorting
		this.sortByDropdown = [
			...this.fieldsForDropdown.filter((e: any) => {
				return e.type !== "list-multiple" && e.type !== "text-area" && e.type !== "text"
			})
		]
	}

	private get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => (item.title ? null : "Please enter a title")
			},
			{
				key: "folder",
				label: "Folder",
				sortable: true,
				dataType: "text",
				dynamicComponent: SuggestList,
				componentProperties: {
					placeholder: "View Folder",
					selectOptions: this.folders ? this.folders.map(folder => folder.title) : []
				}
			},
			{
				key: "teams",
				hasTemplate: true,
				label: "Teams",
				dataType: "list-multiple",
				valueColumn: "viewTeams",
				options: this.teams,
				inTable: false,
				placeholder: "Any"
			},
			{
				key: "groupByLabel",
				label: "Group By",
				sortable: true,
				dataType: "list",
				valueColumn: "groupBy",
				options: this.groupByDropdown
			},
			{
				key: "groupByOption",
				label: "Group",
				dataType: "list",
				options: (view: any) => {
					if (!view.groupBy) return []
					const groupByField = this.groupByDropdown.find(f => f.id === view.groupBy)
					if (!groupByField || !groupByField.fieldDataType || !groupByOptions[groupByField.fieldDataType]) return []

					return groupByOptions[groupByField.fieldDataType]!.map(o => {
						return {
							id: o.value,
							title: o.label
						}
					})
				},
				inTable: false,
				inModal: (view: any) => {
					if (!view.groupBy) return false
					const groupByField = this.groupByDropdown.find(f => f.id === view.groupBy)
					if (!groupByField || !groupByField.fieldDataType || !groupByOptions[groupByField.fieldDataType]) return false

					return groupByOptions[groupByField.fieldDataType]!.length > 0
				},
				tooltip: "Used to group Cases together in the View"
			},
			{
				key: "sortByLabel",
				label: "Sort By",
				dataType: "list",
				valueColumn: "sortBy",
				options: this.sortByDropdown,
				sortable: true,
				errors: (item: any) =>
					item.sortBy ? null : "Please select a field to sort by",
				tooltip: "Used to sort the Cases in the View",
				cssClass: "control-prefix"
			},
			{
				key: "sortByDescending",
				label: "Descending",
				dataType: "checkbox",
				valueColumn: "sortByDescending",
				inTable: false,
				cssClass: "control-suffix"
			},
			{
				key: "columns",
				label: "Columns",
				dataType: "list-multiple",
				valueColumn: "columns",
				dynamicComponent: ColumnSelect,
				componentProperties: {
					columns: this.fields,
					formattingRules: this.editView ? this.editView!.formattingRules : {}
				},
				eventHandlers: {
					inputFormatting: (rules: { [key: string]: FormattingRule[] }) => this.editView!.formattingRules = rules
				},
				inTable: false,
				inModal: () => true,
				sortable: true,
				errors: (item: any) =>
					item.columns && item.columns.length > 0 ? null : "Please select columns for this view"
			},
			{
				key: "clauses",
				label: "Filters",
				inTable: false,
				dynamicComponent: InsightFilters,
				componentProperties: {
					fields: this.filteredFields,
					userFields: this.userFields
				}
			},
			{
				key: "extraValidation",
				label: "Validation",
				inModal: () => false,
				inTable: false,
				errors: (item: any) => {
					let invalid = false
					if (item.clauses && item.clauses.length > 0) {
						item.clauses.forEach((clause: Clause) => {
							if (clause.orClause) {
								if (!clause.isValid || !clause.subClauses || clause.subClauses.length === 0) {
									invalid = true
								}
							} else {
								const operatorsRequireValue = [0, 1, 2, 3]
								if (!clause.field ||
									clause.operator === null ||
									clause.operator === undefined ||
									(operatorsRequireValue.includes(clause.operator) && (clause.value === null || clause.value === undefined))) {
									invalid = true
								}
							}
						})
					}
					return invalid
				}
			}
		]
	}

	// Retrieve all views
	private async loadViews () {
		this.loading = true
		this.views = await api.caseViewsGet()
		this.loading = false
	}

	// Retrieve all folders
	private async loadFolders () {
		this.folders = await api.foldersGet(FolderType.View)
	}

	private createView () {
		return { id: null, title: "", clauses: [], teams: [], viewTeams: [] }
	}

	public async saveView (view: View) {
		view.clauses.forEach(clause => {
			if (clause.value) {
				if ((clause.value as any) instanceof Date) {
					// Ensure Dates are stored in ISO format
					clause.value = ((clause.value as any) as Date).toISOString()
				} else if (!isNaN(Number(clause.value))) {
					// Convert Numbers to strings, otherwise it won't deserialize correctly
					clause.value = clause.value.toString()
				}
			}
		})

		const sortByField = this.fields.find(f => f.key === view.sortBy)
		view.sortByLabel = sortByField ? sortByField.label : view.sortBy

		const groupByField = this.fields.find(f => f.key === view.groupBy)
		view.groupByLabel = groupByField ? groupByField.label : view.groupBy
		view.groupByDataType = groupByField && groupByField.fieldDataType
			? groupByField.fieldDataType
			: groupByField && dataTypeByFieldType[groupByField.dataType]
				? dataTypeByFieldType[groupByField.dataType]
				: null

		// Add assigned teams to teams property
		view.teams = view.viewTeams ? view.viewTeams.map((team: any) => {
			if (team.id) {
				return {
					id: team.id,
					title: team.title
				}
			} else {
				const teamDetails = this.teams.find(f => f.id === team)
				if (teamDetails) {
					return {
						id: teamDetails.id,
						title: teamDetails.title
					}
				} else {
					return null
				}
			}
		}).filter((team: any) => team) : []

		await api.caseUpdateView(view)
		await this.loadViews()

		if (view.folder && view.folder !== "") {
			if (!this.folders.find(folder => folder.title === view.folder)) {
				this.loadFolders()
			}
		}
	}

	private async deleteView (view: View) {
		await api.caseDeleteView(view)
		await this.loadViews()
	}

	public getFieldNameByFieldKey (fieldKey: string) {
		const field = this.fields.find((e: any) => e.key === fieldKey)
		return field ? field.label : ""
	}

	// Add clause when user wants to add new filter option
	public addClause (view: View) {
		view.clauses.push({
			id: Guid.newGuid(),
			dynamicField: false,
			dynamicValue: false,
			field: undefined,
			label: "",
			fieldDataType: 0,
			operator: undefined,
			value: undefined,
			valueLabel: "",
			definitionFieldDataType: undefined,
			searchClauseParameters: getDefaultSearchClauseParameters()
		})
	}

	// Remove clause when user want to remove filter option
	public removeClause (clause: Clause, view: View) {
		view.clauses.removeWhere(
			(e: any) => e.id === clause.id
		)
	}

	// Method to update clause based on selected field
	public fieldSelected (field: any, clause: Clause) {
		// Initiall set to undefined to hide value field and empty operator field
		clause.field = undefined
		clause.operator = undefined
		clause.value = undefined
		clause.valueLabel = undefined
		if (field === null) {
			return
		}

		// Populate with field information
		clause.field = field.key
		clause.label = field.label
		clause.operator = field.operator
		clause.dataSourceId = field.dataSourceId ? field.dataSourceId : null
		clause.fieldDataType = field.comparisonType ? field.comparisonType : this.fieldTypesById[field.dataType].type
		clause.dynamicField = field.dynamicField
		clause.definitionId = field.definitionId
		clause.definitionField = undefined
		clause.definitionFieldLabel = undefined
		clause.definitionFieldDataType = undefined
		clause.itemLabel = field.listLabel
	}

	// Update clause when value is selected
	public valueSelected (item: any, clause: Clause) {
		if (clause.fieldDataType === DataType.arrayMultiple) {
			clause.valueLabel = item.label
			clause.value = item.id
		} else {
			clause.valueLabel = item.label
			clause.value = item.id
		}

		clause.dynamicValue = !!item.dynamicValue
	}

	// Data definition field selected, update clause
	public definitionFieldSelected (field: any, clause: Clause) {
		clause.definitionField = field.key
		clause.definitionFieldLabel = field.label
		clause.definitionFieldDataType = field.comparisonType ? field.comparisonType : this.fieldTypesById[field.dataType].type
		clause.operator = field.operator
		clause.dataSourceId = field.dataSourceId
	}

	// Data definition value selected, update clause
	public definitionValueSelected (field: any, clause: any) {
		clause.value = field.id
		clause.valueLabel = field.label
	}

	// Method to run when loading a modal
	public async loadItem (view: View) {
		Vue.set(view, "viewTeams", view.teams ? this.teams.filter(t => view.teams.some(ut => ut.id === t.id)) : [])

		const loadView = cloneDeep(view)
		this.editView = loadView

		return loadView
	}

	private async definitionDropDownOptions (key: string) {
		let defintionDetails = await api.definitionDetails(key)
		let fieldIds: string[] = []

		defintionDetails.fields.forEach((field: any) =>
			fieldIds.push(field.field.id)
		)

		return this.fields.filter((f: any) => fieldIds.includes(f.key))
	}

	private async areaDropDownOptions () {
		let areas = await api.areaList()

		return areas.map((e: any) => {
			return {
				label: e.label,
				id: e.id
			}
		})
	}

	private async statusDropDownOptions () {
		let statuses = await api.statuses()

		return statuses.map((e: any) => {
			return {
				label: e.title,
				id: e.id
			}
		})
	}

	private async severityDropDownOptions () {
		let severities = await api.severities()

		return severities.map((e: any) => {
			return {
				label: e.title,
				id: e.id
			}
		})
	}

	private async templateDropDownOptions () {
		let templates = await api.categories()

		return templates.map((e: any) => {
			return {
				label: e.title,
				id: e.id
			}
		})
	}

	private foldersUpdated () {
		this.loadViews()
		this.loadFolders()
	}
}
